import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { auth, db } from '../config/firebase';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { DEFAULT_PERMISSIONS } from '../utils/permissions';

export const authService = {
  signup: async (email, password, userData) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const uid = userCredential.user.uid;

      await updateProfile(userCredential.user, {
        displayName: userData.displayName || userData.fullName,
      });

      const now = new Date();
      await setDoc(doc(db, 'users', uid), {
        uid,
        email,
        fullName: userData.fullName,
        displayName: userData.displayName || userData.fullName,
        phone: userData.phone || '',
        country: userData.country || 'United States',
        state: userData.state || 'California',
        profileImage: `https://ui-avatars.com/api/?name=${encodeURIComponent(userData.displayName || userData.fullName)}`,
        currentRole: userData.selectedRole || 'member',
        status: 'pending',
        emailVerified: false,
        mountainOfInfluence: userData.mountainOfInfluence || 'religion',
        assignedCell: null,
        assignedChapter: null,
        assignedComb: null,
        assignedColony: null,
        assignedTerritory: userData.territoryId || null,
        permissions: DEFAULT_PERMISSIONS[userData.selectedRole || 'member'],
        roleHistory: [
          {
            role: userData.selectedRole || 'member',
            startDate: now.getTime(),
            promotedBy: 'system',
          },
        ],
        createdAt: now.getTime(),
        updatedAt: now.getTime(),
        lastLogin: now.getTime(),
        joinedDate: now.getTime(),
      });

      return { uid, email };
    } catch (error) {
      throw new Error(error.message);
    }
  },

  signin: async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      // Note: lastLogin update removed due to Firestore permission issues
      // User document already created during signup
      return userCredential.user;
    } catch (error) {
      throw new Error(error.message);
    }
  },

  signout: async () => {
    try {
      await signOut(auth);
    } catch (error) {
      throw new Error(error.message);
    }
  },

  sendPasswordReset: async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      return true;
    } catch (error) {
      throw new Error(error.message);
    }
  },

  getCurrentUser: () => {
    return auth.currentUser;
  },

  getUserData: async (uid) => {
    try {
      const snapshot = await getDoc(doc(db, 'users', uid));
      if (snapshot.exists()) {
        return snapshot.data();
      }
      return null;
    } catch (error) {
      throw new Error(error.message);
    }
  },

  updateDisplayName: async (uid, newDisplayName) => {
    try {
      const snapshot = await getDoc(doc(db, 'users', uid));
      if (!snapshot.exists()) {
        throw new Error('User not found');
      }

      const userData = snapshot.data();
      const lastChange = userData.lastDisplayNameChange ? new Date(userData.lastDisplayNameChange) : null;
      const now = new Date();
      const thirtyDaysInMs = 30 * 24 * 60 * 60 * 1000;

      // Check if user can change display name (once per month)
      if (lastChange) {
        const timeSinceLastChange = now - lastChange;
        if (timeSinceLastChange < thirtyDaysInMs) {
          const daysRemaining = Math.ceil((thirtyDaysInMs - timeSinceLastChange) / (24 * 60 * 60 * 1000));
          throw new Error(`You can change your display name again in ${daysRemaining} days`);
        }
      }

      // Update display name in Firebase Auth
      await updateProfile(auth.currentUser, {
        displayName: newDisplayName,
      });

      // Update display name in Firestore
      await setDoc(doc(db, 'users', uid), {
        ...userData,
        displayName: newDisplayName,
        lastDisplayNameChange: now,
        updatedAt: now,
      });

      return true;
    } catch (error) {
      throw new Error(error.message);
    }
  },
};
